import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AdminService } from 'src/app/service/admin.service';
import { EventsService } from 'src/app/service/events.service';
import { NotificationService } from 'src/app/service/notification.service';
import { UserService } from 'src/app/service/user.service';
import { TranslateService } from '@ngx-translate/core';
import { Event } from 'src/app/model/event';
import { NotificationEmail } from 'src/app/model/notificationEmail';
import { SelectOption } from 'src/app/model/select.option';
import { User } from 'src/app/model/user';
import { PREVIEW_MARK_OPEN, PREVIEW_MARK_CLOSE } from 'src/app/component/variable-template-editor/variable-template-editor.component';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { SubmissionStatus } from 'src/app/enum/submission.status';

const DEFAULT_ALL = 'all';

/**
 * V2 da página "Enviar E-mail" — reescrita inspirada no editor de modelos
 * (`email-template-edit-dialog`): editor à esquerda (assunto + conteúdo com
 * `<app-variable-template-editor>`, filtros de audiência, cópia); prévia
 * à direita (renderizada pelo backend via `showPreviewPersonalizedEmail`).
 *
 * Reusa os endpoints do EventsService da página antiga (`sendPersonalizedEmail`
 * / `showPreviewPersonalizedEmail`) — a lógica de montar `params` e de validar
 * variáveis foi trazida direto pra cá pra começar autocontido; refatoramos
 * conforme a página amadurecer.
 *
 * Página de envio de e-mail personalizado do TPC Chair. Foi refatorada a
 * partir da versão anterior (removida); esta é agora a única.
 */
@Component({
  selector: 'app-event-send-email',
  templateUrl: './event-send-email.component.html',
  styleUrls: ['./event-send-email.component.scss']
})
export class EventSendEmailComponent implements OnInit, OnDestroy {
  event: Event;

  // Editor: assunto + conteúdo. Mesmo formato do `email-template-edit-dialog`
  // (FormControls consumidos pelo `<app-variable-template-editor>`).
  editorForm: FormGroup;
  get subjectControl(): FormControl { return this.editorForm.get('subject') as FormControl; }
  get contentControl(): FormControl { return this.editorForm.get('content') as FormControl; }

  // Prévia inline (read-only, ao lado de cada editor). Mesmos componentes
  // `<app-variable-template-editor>`, alimentados por `previewForm` com as
  // variáveis já substituídas por exemplos.
  previewForm: FormGroup;
  get previewSubjectControl(): FormControl { return this.previewForm.get('previewSubject') as FormControl; }
  get previewContentControl(): FormControl { return this.previewForm.get('previewContent') as FormControl; }
  private livePreviewSub?: Subscription;
  private recipientsRefreshSub?: Subscription;
  private recipientsRefresh$ = new Subject<void>();

  // Filtros de audiência (autores/revisores) — mesma estrutura da página antiga,
  // pra manter os `params` que o backend já entende.
  authorsForm: FormGroup;
  reviewersForm: FormGroup;
  copyToForm: FormGroup;

  // Fontes das opções de select. `tracks` / `paperGroups` / `tpcGroups` mantêm
  // o sentinela `all` p/ os selects SINGLE (revisores por trilha, etc.).
  // `tracksMulti` / `paperGroupsMulti` são os mesmos SEM o `all` — usados nos
  // multi-selects de autores (ausência de seleção = "todos").
  tracks: Array<SelectOption> = [{ id: DEFAULT_ALL, value: 'admin.event.people.send-email.all-tracks' }];
  paperGroups: Array<SelectOption> = [{ id: DEFAULT_ALL, value: 'admin.event.people.send-email.all-groups' }];
  tpcGroups: Array<SelectOption> = [{ id: DEFAULT_ALL, value: 'admin.event.people.send-email.all-groups' }];
  tracksMulti: Array<SelectOption> = [];
  paperGroupsMulti: Array<SelectOption> = [];
  tpcGroupsMulti: Array<SelectOption> = [];

  // Opções fixas de "tipo de revisor" — mesmas 5 categorias da UI antiga,
  // agora consumidas como multi-select (id = chave do backend em lowercase).
  readonly reviewerTypeOptions = [
    { id: 'all_tpc',                  value: 'admin.event.people.send-email.reviewer-type-all-tpc' },
    { id: 'tpc_reviewing_papers',     value: 'admin.event.people.send-email.reviewer-type-tpc-reviewing' },
    { id: 'all_reviewers_conference', value: 'admin.event.people.send-email.reviewer-type-all-reviewers' },
    { id: 'all_reviewers_unfinished', value: 'admin.event.people.send-email.reviewer-type-reviewers-unfinished' },
    { id: 'session_chair',            value: 'admin.event.people.send-email.reviewer-type-session-chair' },
  ];

  // ── Switches de destinatário (autores/revisores) ────────────────────
  // Ambos começam OFF: o usuário faz a escolha explícita. buildParams IGNORA
  // os filtros do lado desligado — permite preparar dois cenários e alternar
  // sem perder configuração.
  authorsEnabled = false;
  reviewersEnabled = false;
  // Rótulo do "Eu mesmo (Nome)" na seção Com cópia.
  currentUserName = '';

  // Filtro de status de submissão do autor — multi-select em modo status-chip
  // (mesma receita do `submissions-field-filter-v2`). `id` é LOWERCASE porque
  // é isso que o backend recebe no payload `authors.paper_status`; `statusClass`
  // pinta o chip pelo CSS global (`_status-chip.scss`); `label` é a chave i18n
  // do rótulo do chip.
  authorStatusOptions: any[] = ['ACCEPTED', 'REJECTED', 'PENDING', 'ACTIVE'].map(v => ({
    id: v.toLowerCase(),
    value: v,
    statusClass: SubmissionStatus.toClass(v as SubmissionStatus),
    icon: SubmissionStatus.icon(v as SubmissionStatus).fontIcon,
    label: SubmissionStatus.toString(v as SubmissionStatus),
  }));

  // Prévia (backend). `preview` armazena a última resposta do
  // `showPreviewPersonalizedEmail`; usada tanto pra contagem/lista de
  // destinatários quanto pra exportação. Refresh automático debounced
  // via `recipientsRefresh$` — não há mais estado "stale" visível.
  preview: Array<NotificationEmail> = [];
  loadingPreview = false;
  sending = false;
  loadingRecipients = false;

  // ── Frase-natural de filtro (Proposta B — em teste) ─────────────────
  // "Enviando para autores de submissões [status] ▾ nas trilhas [tracks] ▾
  // do grupo [paper_groups] ▾." Cada pílula abre um popover com chips
  // clicáveis que espelham (bi-direcionalmente) o estado do `authorsForm`.
  // Coexiste com o card "Autores" atual: alterar aqui reflete lá e vice-versa.
  sentencePillKind:
    | 'status' | 'track' | 'paper_group'
    | 'rev_types' | 'rev_track' | 'rev_paper_group' | 'rev_tpc_group'
    | null = null;
  sentencePillAnchor: { elementRef: ElementRef<HTMLElement> } | null = null;
  sentencePillOpen = false;
  sentencePillPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY: 6 },
    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  // ── Popover inline p/ adicionar destinatário em "Cópia > Outros usuários" ──
  copyUserForm: FormGroup;
  copyUserOpen = false;
  copyUserAnchor: { elementRef: ElementRef<HTMLElement> } | null = null;
  copyUserPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY: 6 },
    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  // Variáveis suportadas — mesma lista da página antiga; virará dinâmica quando
  // migrarmos pro catálogo por gatilho do editor de modelos.
  readonly validVariables: string[] = [
    '{{user_id}}', '{{user_email}}', '{{profile_first_alternative_email}}',
    '{{profile_second_alternative_email}}', '{{user_first_name}}', '{{user_last_name}}',
    '{{event_name}}', '{{event_acronym}}', '{{event_year}}',
    '{{submission_title}}', '{{submission_id}}', '{{submission_page}}',
    '{{submission_extra_items}}', '{{main_page}}', '{{profile_page}}',
    // Incluídas na V2 pra alinhar com o que o painel de labels da UI antiga
    // mostrava — antes viviam só no texto do "labels" (sem estar validadas,
    // o que dava "variável inválida" em templates legítimos).
    '{{submission_domain}}', '{{profile_edit}}'
  ];
  // Passado pro `<app-variable-template-editor>` como sugestões do menu "/".
  emailVariables: Array<SelectOption> = [];

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    private eventsService: EventsService,
    private notificationService: NotificationService,
    private userService: UserService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Carrega nome do usuário atual pro rótulo "Eu mesmo (Nome)" na Cópia.
    this.userService.getUserFromToken().subscribe(u => {
      this.currentUserName = u?.name || '';
    });

    setTimeout(() => {
      this.event = this.adminService.selectedEvent;
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.initForms();
          this.loadSelectOptions();
        });
      } else {
        this.initForms();
        this.loadSelectOptions();
      }
    });
  }

  ngOnDestroy(): void {
    this.livePreviewSub?.unsubscribe();
    this.recipientsRefreshSub?.unsubscribe();
  }

  onAudienceSwitchChange(): void {
    this.recipientsRefresh$.next();
  }

  private initForms(): void {
    this.editorForm = this.fb.group({
      subject: ['[{{event_acronym}} {{event_year}}] - ', [Validators.required]],
      content: ['', [Validators.required]],
    });
    this.previewForm = this.fb.group({ previewSubject: [''], previewContent: [''] });
    this.authorsForm = this.fb.group({
      // Todos os filtros de AUTORES agora são multi-select — ausência = "todos".
      // Valores saem como arrays: `[]` (omitido do payload) ou `[id, id, …]`.
      track: [[] as any[]],
      paper_group: [[] as any[]],
      paper_status: [[] as string[]],
    });
    this.reviewersForm = this.fb.group({
      // Todos os filtros de REVISORES em multi-select (arrays). Ausência = todos
      // do respectivo eixo. `reviewers_filter` mudou de FormGroup de booleanos
      // para array de ids selecionados (ex.: ['all_tpc','session_chair']).
      track: [[] as any[]],
      tpc_group: [[] as any[]],
      paper_group: [[] as any[]],
      reviewers_filter: [['all_reviewers_conference']],
    });
    this.copyToForm = this.fb.group({
      myself: [false], chair: [false], users: [[] as User[]]
    });
    this.copyUserForm = this.fb.group({ user: ['', Validators.required] });

    // Invalidar a contagem cacheada quando QUALQUER filtro muda + disparar
    // um refresh automático (debounced) do preview — o usuário não precisa
    // clicar em "Ver destinatários" pra ter contagem atualizada.
    [this.authorsForm, this.reviewersForm, this.copyToForm].forEach(f => {
      f.valueChanges.subscribe(() => this.recipientsRefresh$.next());
    });
    this.recipientsRefreshSub = this.recipientsRefresh$
      .pipe(debounceTime(400))
      .subscribe(() => this.getPreview());
    // Primeira contagem ao abrir a página.
    setTimeout(() => this.recipientsRefresh$.next(), 0);

    // Popula o menu "/" do variable-template-editor com o catálogo local.
    // Descrições vêm do dicionário `admin.event.people.send-email.labels`
    // (formato "{{name}} : Explicação" — extraímos a parte após " : ").
    const labelKeyById: { [id: string]: string } = {
      user_id: 'user-id', user_email: 'user-email',
      profile_first_alternative_email: 'first-alt-email',
      profile_second_alternative_email: 'second-alt-email',
      user_first_name: 'first-name', user_last_name: 'last-name',
      event_name: 'event-name', event_acronym: 'event-acronym', event_year: 'event-year',
      submission_title: 'submission-title', submission_id: 'submission-id',
      submission_page: 'submission-page', submission_extra_items: 'submission-extra-items',
      main_page: 'submission-domain', profile_page: 'profile-edit',
      submission_domain: 'submission-domain', profile_edit: 'profile-edit',
    };
    this.translate.get('admin.event.people.send-email.labels').subscribe(labels => {
      this.emailVariables = this.validVariables.map(v => {
        const key = v.slice(2, -2);
        const raw = (labels && labels[labelKeyById[key]]) || key;
        const idx = raw.indexOf(':');
        const desc = idx >= 0 ? raw.slice(idx + 1).trim() : raw;
        return new SelectOption(key, desc);
      });
    });

    // Prévia AO VIVO: a cada edição, recomputa a substituição de variáveis
    // e joga nos campos-espelho (previewForm, read-only). Debounce curto p/
    // agrupar teclas sem perder a sensação de "imediato".
    this.updatePreview();
    this.livePreviewSub = this.editorForm.valueChanges
      .pipe(debounceTime(80))
      .subscribe(() => this.updatePreview());
  }

  // Substitui as variáveis em SUBJECT/CONTENT com valores de exemplo e joga
  // nos campos-espelho da prévia. Mesma técnica do `email-template-edit-dialog`:
  // envolve cada valor SUBSTITUÍDO com sentinelas (PREVIEW_MARK_*) → o
  // `<app-variable-template-editor>` renderiza em azul.
  private updatePreview(): void {
    if (!this.previewForm) { return; }
    const subject = this.editorForm.value.subject || '';
    const content = this.editorForm.value.content || '';
    const ctx = this.buildPreviewContext();
    // Envolve cada valor substituído com sentinelas PREVIEW_MARK_* — o
    // `<app-variable-template-editor>` (usado nas duas prévias) as renderiza
    // como chips azuis. Não-resolvidos ficam literais.
    const mark = (v: string) => PREVIEW_MARK_OPEN + v + PREVIEW_MARK_CLOSE;
    const regex = /\{\{\s*([^{}]+?)\s*\}\}/g;
    const substitute = (text: string) => text.replace(regex, (matched, inner: string) => {
      const path = inner.trim();
      const value = this.resolvePath(ctx, path);
      if (value !== undefined && value !== null && typeof value !== 'object') {
        return mark(String(value));
      }
      return matched;
    });
    this.previewForm.patchValue({
      previewSubject: substitute(subject),
      previewContent: substitute(content),
    });
  }

  // Contexto ilustrativo p/ substituição de variáveis SEM backend: `event`
  // é REAL; o resto é exemplo. Nomes underscore (do catálogo local) são
  // mapeados aqui. Refinaremos ao migrar pro catálogo dinâmico por gatilho.
  private buildPreviewContext(): any {
    const origin = window.location.origin;
    const ev: any = this.event || {};
    return {
      // — planos (underscore) casando com validVariables —
      user_id: '42',
      user_email: 'ana@exemplo.com',
      profile_first_alternative_email: 'ana.pereira@exemplo.com',
      profile_second_alternative_email: '',
      user_first_name: 'Ana',
      user_last_name: 'Pereira',
      event_name: ev.name || 'Nome do evento',
      event_acronym: ev.acronym || 'ACR',
      event_year: ev.year || new Date().getFullYear().toString(),
      submission_title: 'Exemplo de título de submissão',
      submission_id: '70654',
      submission_page: `${origin}/submissions/70654/`,
      submission_extra_items: '',
      submission_domain: `${origin}/${ev.uri || 'evento'}/`,
      profile_edit: `${origin}/profile/edit/`,
      main_page: `${origin}/${ev.uri || 'evento'}/`,
      profile_page: `${origin}/profile/`,
    };
  }

  private resolvePath(ctx: any, path: string): any {
    return path.split('.').reduce((obj, key) => (obj == null ? undefined : obj[key]), ctx);
  }

  private loadSelectOptions(): void {
    const trackOpts = this.event.tracks.map(t => new SelectOption(t.id, t.name));
    this.tracks.push(...trackOpts);
    this.tracksMulti = trackOpts;

    this.eventsService.getEventPaperGroups(this.event.id).subscribe(gs => {
      const opts = gs.map(g => new SelectOption(g.id, g.name));
      this.paperGroups.push(...opts);
      this.paperGroupsMulti = opts;
    });
    this.eventsService.getEventTPCGroups(this.event.id, 'minimum').subscribe(gs => {
      const opts = gs.map(g => new SelectOption(g.id, g.name));
      this.tpcGroups.push(...opts);
      this.tpcGroupsMulti = opts;
    });
  }

  // ───────────────────────────────────────────────────────────────
  //  Envio / prévia
  // ───────────────────────────────────────────────────────────────

  submit(): void {
    const invalid = this.validateVariables();
    if (invalid.length) {
      this.notificationService.notifyError('admin.email-template.invalid-variables',
        { params: { variables: invalid.join(', ') } });
      return;
    }
    this.sending = true;
    this.eventsService.sendPersonalizedEmail(this.event.id, this.buildParams()).subscribe(
      () => {
        this.sending = false;
        this.notificationService.notify('admin.event.people.send-email.send-succeeded');
      },
      () => {
        this.sending = false;
        this.notificationService.notifyError('admin.event.people.send-email.send-failed');
      }
    );
  }

  /** Consulta backend p/ saber quem receberá o e-mail com a configuração
   *  atual. Popula `preview` (a `.length` é a contagem exibida no card) e
   *  reseta `previewStale`. Disparado automaticamente (debounced) sempre que
   *  os filtros mudam — a lista fica em sincronia sem clique explícito. */
  getPreview(): void {
    const invalid = this.validateVariables();
    if (invalid.length) {
      this.notificationService.notifyError('admin.email-template.invalid-variables',
        { params: { variables: invalid.join(', ') } });
      return;
    }
    this.loadingPreview = true;
    this.eventsService.showPreviewPersonalizedEmail(this.event.id, this.buildParams()).subscribe(
      preview => {
        this.loadingPreview = false;
        this.preview = preview || [];
      },
      () => { this.loadingPreview = false; }
    );
  }

  // ── Frase-natural: interação com as pílulas ─────────────────────────
  openSentencePill(kind: NonNullable<EventSendEmailComponent['sentencePillKind']>, anchor: HTMLElement): void {
    this.sentencePillKind = kind;
    this.sentencePillAnchor = { elementRef: new ElementRef(anchor) };
    this.sentencePillOpen = true;
  }
  closeSentencePill(): void {
    if (this.sentencePillKind === 'rev_types') {
      const ctrl = this.reviewersForm.get('reviewers_filter');
      if (!ctrl?.value?.length) { ctrl?.setValue(['all_reviewers_conference']); }
    }
    this.sentencePillOpen = false;
    this.sentencePillAnchor = null;
    this.sentencePillKind = null;
  }
  /** Opções renderizadas no popover pra pill ativa. Chips coloridos p/ status;
   *  linhas simples pra os demais eixos. */
  get sentencePillOptions(): any[] {
    switch (this.sentencePillKind) {
      case 'status':          return this.authorStatusOptions;
      case 'track':           return this.tracksMulti;
      case 'paper_group':     return this.paperGroupsMulti;
      case 'rev_types':       return this.reviewerTypeOptions;
      case 'rev_track':       return this.tracksMulti;
      case 'rev_paper_group': return this.paperGroupsMulti;
      case 'rev_tpc_group':   return this.tpcGroupsMulti;
      default:                return [];
    }
  }
  /** Retorna {form, control} pra pill ativa — mapa único usado pelo isSelected/toggle. */
  private get sentencePillTarget(): { form: FormGroup, name: string } | null {
    switch (this.sentencePillKind) {
      case 'status':          return { form: this.authorsForm,   name: 'paper_status' };
      case 'track':           return { form: this.authorsForm,   name: 'track' };
      case 'paper_group':     return { form: this.authorsForm,   name: 'paper_group' };
      case 'rev_types':       return { form: this.reviewersForm, name: 'reviewers_filter' };
      case 'rev_track':       return { form: this.reviewersForm, name: 'track' };
      case 'rev_paper_group': return { form: this.reviewersForm, name: 'paper_group' };
      case 'rev_tpc_group':   return { form: this.reviewersForm, name: 'tpc_group' };
      default:                return null;
    }
  }
  isSentenceOptionSelected(id: any): boolean {
    const tgt = this.sentencePillTarget;
    if (!tgt) { return false; }
    const arr: any[] = tgt.form.get(tgt.name)?.value || [];
    return arr.indexOf(id) !== -1;
  }
  toggleSentenceOption(id: any): void {
    const tgt = this.sentencePillTarget;
    if (!tgt) { return; }
    const arr: any[] = [...(tgt.form.get(tgt.name)?.value || [])];
    const i = arr.indexOf(id);
    if (i === -1) { arr.push(id); } else { arr.splice(i, 1); }
    tgt.form.get(tgt.name)?.setValue(arr);
  }
  /** Rótulo exibido dentro de cada pílula. Vazio = "todos/todas" (ou "selecionar"
   *  no caso de rev_types que é opt-in); 1-2 selecionados = lista nomes;
   *  3+ = "N selecionados". */
  sentencePillLabel(kind: NonNullable<EventSendEmailComponent['sentencePillKind']>): string {
    const cfg = this.pillMeta(kind);
    if (!cfg) { return ''; }
    const arr: any[] = cfg.form.get(cfg.name)?.value || [];
    if (!arr.length) { return this.translate.instant(cfg.emptyLabel); }
    // Sempre lista TODOS os itens selecionados, um por um — sem truncar em
    // "N selecionados" quando há vários. A pill cresce conforme a seleção.
    return arr
      .map(id => cfg.options.find((o: any) => o.id === id))
      .filter(Boolean)
      .map((o: any) => {
        // `label` (chave i18n do chip de status) e `value` (chave i18n dos
        // tipos de revisor) precisam passar por translate. Tracks/paper-
        // groups têm value = nome puro; translate é no-op nesse caso.
        const raw = o.label || o.value || o.name || String(o.id);
        return this.translate.instant(raw);
      })
      .join(', ');
  }
  private pillMeta(kind: NonNullable<EventSendEmailComponent['sentencePillKind']>):
      { form: FormGroup, name: string, options: any[], emptyLabel: string } | null {
    switch (kind) {
      case 'status':          return { form: this.authorsForm,   name: 'paper_status',     options: this.authorStatusOptions, emptyLabel: 'admin.event.people.send-email.pill-all-status' };
      case 'track':           return { form: this.authorsForm,   name: 'track',            options: this.tracksMulti,         emptyLabel: 'admin.event.people.send-email.pill-all-tracks' };
      case 'paper_group':     return { form: this.authorsForm,   name: 'paper_group',      options: this.paperGroupsMulti,    emptyLabel: 'admin.event.people.send-email.pill-all-groups' };
      case 'rev_types':       return { form: this.reviewersForm, name: 'reviewers_filter', options: this.reviewerTypeOptions, emptyLabel: 'admin.event.people.send-email.pill-select' };
      case 'rev_track':       return { form: this.reviewersForm, name: 'track',            options: this.tracksMulti,         emptyLabel: 'admin.event.people.send-email.pill-all-tracks' };
      case 'rev_paper_group': return { form: this.reviewersForm, name: 'paper_group',      options: this.paperGroupsMulti,    emptyLabel: 'admin.event.people.send-email.pill-all-groups' };
      case 'rev_tpc_group':   return { form: this.reviewersForm, name: 'tpc_group',        options: this.tpcGroupsMulti,      emptyLabel: 'admin.event.people.send-email.pill-all-tpc-groups' };
      default:                return null;
    }
  }

  // ── Cópia > "Outros usuários" (chips + popover inline) ──────────────
  openAddCopyUser(anchor: HTMLElement): void {
    this.copyUserAnchor = { elementRef: new ElementRef(anchor) };
    this.copyUserOpen = true;
  }
  closeAddCopyUser(): void {
    this.copyUserOpen = false;
    this.copyUserAnchor = null;
    this.copyUserForm?.get('user')?.setValue([]);
  }
  onCopyUserPicked(picked: User): void {
    this.closeAddCopyUser();
    if (!picked) { return; }
    const current = this.copyToForm.value.users || [];
    if (current.some((u: User) => u.id === picked.id)) { return; }
    this.copyToForm.patchValue({ users: [...current, picked] });
  }
  removeCopyUser(user: User): void {
    const current = this.copyToForm.value.users || [];
    this.copyToForm.patchValue({ users: current.filter((u: User) => u.id !== user.id) });
  }
  focusCopyUserInput(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.querySelector('.copy-user-popover input') as HTMLInputElement;
      if (el) { el.focus(); el.select?.(); }
    }));
  }

  exportRecipients(): void {
    const invalid = this.validateVariables();
    if (invalid.length) {
      this.notificationService.notifyError('admin.email-template.invalid-variables',
        { params: { variables: invalid.join(', ') } });
      return;
    }
    this.loadingRecipients = true;
    this.eventsService.showPreviewPersonalizedEmail(this.event.id, this.buildParams()).subscribe(preview => {
      this.loadingRecipients = false;
      if (!preview?.length) { return; }
      const emails = preview
        .flatMap(m => [...(m.to || []), ...(m.cc || [])])
        .filter((v, i, s) => s.indexOf(v) === i);
      const blob = new Blob([emails.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'emails_list.txt';
      a.click();
      URL.revokeObjectURL(url);
    }, () => { this.loadingRecipients = false; });
  }

  // ───────────────────────────────────────────────────────────────
  //  Auxiliares (portados da página antiga, mantidos autocontidos)
  // ───────────────────────────────────────────────────────────────

  private buildParams(): any {
    const params: any = this.editorForm.getRawValue();
    params.authors = {};
    params.reviewers = {};

    // Autores: switch OFF → nenhum filtro contribui. Switch ON + filtros vazios
    // = todos os autores. Cada filtro é multi-select (array); ausência = todos
    // do eixo.
    if (this.authorsEnabled) {
      const authorTracks: any[] = this.authorsForm.value.track || [];
      if (authorTracks.length) { params.authors.track = authorTracks; }
      const authorGroups: any[] = this.authorsForm.value.paper_group || [];
      if (authorGroups.length) { params.authors.paper_group = authorGroups; }
      const paperStatus: string[] = this.authorsForm.value.paper_status || [];
      // Pill vazio = "qualquer status" → backend precisa receber TODOS os ids
      // (o endpoint `showEmails/` exclui autores quando `paper_status` está
      // ausente). Rótulo diz "qualquer"; enviamos todos p/ cumprir a promessa.
      params.authors.paper_status = paperStatus.length
        ? paperStatus
        : this.authorStatusOptions.map(o => o.id);
    }

    // Revisores: switch OFF → não envia; sempre precisa `reviewers_filter`
    // (array de tipos) preenchido pra alguém receber — vazio = nenhum revisor.
    if (this.reviewersEnabled) {
      const revTracks: any[] = this.reviewersForm.value.track || [];
      if (revTracks.length) { params.reviewers.track = revTracks; }
      const revPaperGroups: any[] = this.reviewersForm.value.paper_group || [];
      if (revPaperGroups.length) { params.reviewers.paper_group = revPaperGroups; }
      const revTpcGroups: any[] = this.reviewersForm.value.tpc_group || [];
      if (revTpcGroups.length) { params.reviewers.tpc_group = revTpcGroups; }
      params.reviewers.reviewers_filter = this.reviewersForm.value.reviewers_filter || [];
    } else {
      // Backend antigo espera `reviewers_filter` sempre presente; mantemos vazio.
      params.reviewers.reviewers_filter = [];
    }

    params.copy_to = this.copyToForm.getRawValue();
    return params;
  }

  /** Habilitação do botão Enviar: precisa haver AO MENOS UM destinatário no TO
   *  — autores ligado, revisores ligado, OU lista de pessoas específicas
   *  não-vazia. Cópia (chairs/eu mesmo) sozinha não conta. Também exige
   *  editorForm válido. */
  get canSend(): boolean {
    if (!this.editorForm?.valid) { return false; }
    const extras: User[] = this.copyToForm?.value?.users || [];
    return this.authorsEnabled || this.reviewersEnabled || extras.length > 0;
  }

  private validateVariables(): string[] {
    const text = `${this.editorForm.value.subject || ''} ${this.editorForm.value.content || ''}`;
    const regex = /\{\{([^{}]+?)\}\}/g;
    const invalid: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
      const clean = `{{${m[1].trim()}}}`;
      if (!this.validVariables.includes(clean) && !invalid.includes(m[0])) {
        invalid.push(m[0]);
      }
    }
    return invalid;
  }
}
